"use client"

import { usePathname } from "next/navigation"
import React from "react"

// Components
import IconMinus from "@/app/(site)/_components/Atoms/Icons/IconMinus"
import IconPlus from "@/app/(site)/_components/Atoms/Icons/IconPlus"
import {
    AccordionContent,
    AccordionIcon,
    AccordionRoot,
    AccordionTrigger
} from "../../../Atoms/Accordion"
import Heading from "../../../Atoms/Heading"
import StoryText from "../../../Atoms/Storytext"
import Text from "../../../Atoms/Text"

// Utils
import { Detail } from "@/app/(site)/_utils/transformData/transformTechnicalDetails"

// Translations
import { getLangCode } from "@/app/(site)/_utils/misc"
import { t } from "@/translations"
import { ImageType } from "@/types"
import BaseImage from "../../../Atoms/BaseImage"

type AccordionTechnicalDetailsProps = {
    technicalDetails: Detail[]
    images: ImageType[]
}

const AccordionTechnicalDetails: React.FC<AccordionTechnicalDetailsProps> = ({
    technicalDetails,
    images
}) => {
    const langCode = getLangCode(usePathname().split("/")[1]) || ""

    return (
        <AccordionRoot className="border-t-2 border-black" initalState={false}>
            <AccordionTrigger className="grid w-full cursor-pointer grid-cols-2 pb-32 pt-24 md:pb-72">
                <Heading type="accordionHeading" className="text-left">
                    {t(langCode, "Technical Details")}
                </Heading>
                <AccordionIcon
                    className="col-start-2 flex justify-end"
                    openIcon={<IconMinus />}
                    closeIcon={<IconPlus />}
                />
            </AccordionTrigger>
            <AccordionContent className="mb-24 md:mb-72">
                <div className="grid grid-cols-3 gap-24">
                    {/* 3 Colum Row */}
                    <div className="col-span-3 md:col-span-1">
                        <div className="col-span-3 grid grid-cols-2 gap-16 md:col-span-1 md:grid-cols-1 md:gap-0">
                            {/* 1 Column */}
                            {images &&
                                images.map((image, index) => {
                                    const imageAlt = image.alt ?? ""
                                    const imageCaption = image.caption ?? ""

                                    return (
                                        <div
                                            key={index}
                                            className="col-span-1 cursor-pointer md:mb-24 md:w-1/2"
                                        >
                                            <div className="relative mb-8 aspect-3/2 w-full">
                                                <BaseImage
                                                    src={image?.asset?._ref || ""}
                                                    mimeType={
                                                        image?.asset?.mimeType || ""
                                                    }
                                                    sizes="(min-width: 968px) 16.6667vw, 100vw"
                                                    alt={imageAlt}
                                                    fill
                                                    className="object-cover"
                                                />
                                            </div>
                                            <Text size="small" weight="regular">
                                                {imageCaption}
                                            </Text>
                                        </div>
                                    )
                                })}
                        </div>
                    </div>
                    <div className="col-span-3 mt-24 md:col-span-2 md:mt-0">
                        {/* 2 Column  */}
                        <div className="grid grid-cols-1 gap-0 md:grid-cols-2 md:gap-24">
                            {technicalDetails?.map((bulletPoint, index) => {
                                const title = bulletPoint?.title
                                const description = bulletPoint?.description
                                return (
                                    <div
                                        key={index}
                                        className="col-span-1 grid grid-cols-2 border-t border-black pb-24 pt-12 md:pb-48 md:pt-8"
                                    >
                                        {/* One bulletpoint */}
                                        <Text
                                            size="medium"
                                            weight="regular"
                                            className="col-span-2 mb-12 sm:col-span-1 md:mb-0"
                                        >
                                            {title}
                                        </Text>
                                        <StoryText
                                            size="size4"
                                            className="col-span-2 sm:col-span-1"
                                        >
                                            {description}
                                        </StoryText>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </AccordionContent>
        </AccordionRoot>
    )
}

export default AccordionTechnicalDetails
